import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { Image, User, Clock, CheckCircle, XCircle, Copy, MessageSquare, GripVertical } from "lucide-react";
import type { IssuePerson, IssueReport, IssueStatus } from "../../types/issue-triage";
import { ISSUE_SEVERITY_CONFIG } from "../../types/issue-triage";
import { formatDistanceToNow } from "date-fns";
import { OwnerPicker } from "./OwnerPicker";
import { MoveSelect } from "./MoveSelect";

interface IssueCardProps {
  issue: IssueReport;
  onClick: () => void;
  onApprove?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onCopy?: (id: string) => void;
  /** Assignable developers; with onOwnerChange the card shows an owner picker. */
  owners?: IssuePerson[];
  onOwnerChange?: (id: string, owner: IssuePerson | null) => void;
  /** With this, the card shows a Move-to select (every stage-to-stage move, L-1224). */
  onMove?: (issue: IssueReport, status: IssueStatus) => void;
}

export function IssueCard({ issue, onClick, onApprove, onDismiss, onCopy, owners, onOwnerChange, onMove }: IssueCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: issue.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const severityConfig = ISSUE_SEVERITY_CONFIG[issue.severity || "medium"];
  const timeAgo = issue.createdAt?.toDate
    ? formatDistanceToNow(issue.createdAt.toDate(), { addSuffix: true })
    : "recently";

  const notesCount = issue.statusNotes?.length ?? 0;
  const latestNote = notesCount > 0 ? issue.statusNotes![notesCount - 1] : null;

  const isReviewable = issue.status === "needs_review";
  // Reviewed = the developers' pickup queue: copying from it starts the work.
  const isPickup = issue.status === "reviewed";
  const showActions = (isReviewable && (onApprove || onDismiss || onCopy)) ||
    (!isReviewable && ["reviewed", "in_progress"].includes(issue.status) && onCopy);

  // Extract module from route (e.g., "/field-service/forms/..." → "field-service")
  const routePath = issue.context?.route || issue.context?.url || "";
  const moduleMatch = routePath.match(/^\/([a-z-]+)/);
  const moduleName = moduleMatch ? moduleMatch[1] : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-300 shadow-sm p-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all group relative ${isDragging ? "opacity-50 shadow-lg z-50" : ""}`}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 p-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>
      {/* Top row: Issue code + severity + module + icons */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {issue.issueCode && (
            <span className="font-mono text-[10px] font-bold text-slate-400">{issue.issueCode}</span>
          )}
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${severityConfig.bgColor} ${severityConfig.textColor}`}>
            {severityConfig.label}
          </span>
          {moduleName && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 text-blue-600">
              /{moduleName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(issue.screenshotUrl || (issue.attachmentUrls?.length ?? 0) > 0) && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-400" title={`${(issue.screenshotUrl ? 1 : 0) + (issue.attachmentUrls?.length ?? 0)} image(s) attached`}>
              <Image className="w-3.5 h-3.5" />
              {(issue.screenshotUrl ? 1 : 0) + (issue.attachmentUrls?.length ?? 0)}
            </span>
          )}
        </div>
      </div>

      {/* Title */}
      <h4 className="text-sm font-medium text-slate-900 line-clamp-2 mb-2">
        {issue.title}
      </h4>

      {/* Owner + who picked it up */}
      {(onOwnerChange || issue.owner || issue.pickedUpBy) && (
        <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
          {onOwnerChange && owners ? (
            <OwnerPicker
              compact
              owner={issue.owner}
              owners={owners}
              onChange={(next) => onOwnerChange(issue.id, next)}
            />
          ) : (
            <span>Owner: {issue.owner?.name ?? "Unowned"}</span>
          )}
          {issue.status === "in_progress" && issue.pickedUpBy && (
            <span className="truncate" title={`Picked up by ${issue.pickedUpBy.name}`}>
              Started by {issue.pickedUpBy.name}
            </span>
          )}
        </div>
      )}

      {onMove && (
        <div className="mb-2">
          <MoveSelect compact issue={issue} onMove={(next) => onMove(issue, next)} />
        </div>
      )}

      {/* Meta */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <div className="flex items-center gap-1">
          <User className="w-3 h-3" />
          <span className="truncate max-w-[100px]">{issue.reportedBy?.email?.split("@")[0] ?? "unknown"}</span>
        </div>
        <span>•</span>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{timeAgo}</span>
        </div>
      </div>

      {/* Confidence indicator for analyzed issues */}
      {issue.analysis && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <span className={`text-xs ${
            issue.analysis.confidence === "high" ? "text-green-600" :
            issue.analysis.confidence === "medium" ? "text-yellow-600" : "text-slate-500"
          }`}>
            {issue.analysis.confidence} confidence
          </span>
        </div>
      )}

      {/* Status Notes indicator */}
      {notesCount > 0 && latestNote && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1 text-xs text-slate-500 mb-0.5">
            <MessageSquare className="w-3 h-3" />
            <span>{notesCount} note{notesCount !== 1 ? "s" : ""}</span>
          </div>
          <p className="text-xs text-slate-400 line-clamp-2" title={latestNote.text}>
            {latestNote.text}
          </p>
        </div>
      )}

      {/* Quick action buttons - visible on hover */}
      {showActions && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 px-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="pointer-events-auto flex items-center gap-1">
            {isReviewable && onApprove && (
              <button
                onClick={(e) => { e.stopPropagation(); onApprove(issue.id); }}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                title="Approve (A)"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Approve
              </button>
            )}
            {isReviewable && onDismiss && (
              <button
                onClick={(e) => { e.stopPropagation(); onDismiss(issue.id); }}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                title="Dismiss (D)"
              >
                <XCircle className="w-3.5 h-3.5" />
                Dismiss
              </button>
            )}
            {onCopy && (
              <button
                onClick={(e) => { e.stopPropagation(); onCopy(issue.id); }}
                className={`flex items-center justify-center gap-1 p-1.5 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors ${!isReviewable ? "flex-1" : ""}`}
                title={isPickup ? "Copy for Claude and move to In Progress" : "Copy for Claude (C)"}
              >
                <Copy className="w-3.5 h-3.5" />
                {!isReviewable && (isPickup ? "Copy & Start" : "Copy for Claude")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
